/**
 * OperatorRequestNote — the OPERATOR's note on a haul request made through the public
 * API (Phase 18 / Task 18.6, DEC-299).
 *
 * The operator's words live in `jobs.operator_request_notes`, never in `dispatcher_notes`,
 * so they can never be mistaken for a dispatcher's — and this component is the other half
 * of that rule: wherever a dispatcher views or assigns a job, the note appears under an
 * explicit "Operator's note" label, visually distinct from the dispatcher's own notes, and
 * read-only (the column is immutable; a dispatcher cannot edit what the operator said).
 *
 * The text is rendered VERBATIM — `white-space: pre-wrap`, no trimming, no case change
 * (DEC-147 notes exception) — because a line break in "gate code 4471\nuse north lease
 * road" is part of the instruction.
 *
 * Renders NOTHING for a job with no operator note, which is every job a dispatcher, driver
 * or foreman created. A whitespace-only value is treated as absent; the API refuses one, so
 * that is defence only.
 *
 * Used by: JobQueue (an always-visible row under the job), JobDetailModal, ForceAssignModal,
 * InReviewDetailPanel, and JobChip (a compact badge plus the full text in its tooltip).
 */

import * as React from 'react';

/** The label every surface uses. One string, so the board never says it two ways. */
export const OPERATOR_NOTE_LABEL = "Operator's note";

/**
 * Violet: no existing token, and deliberately none of the status/selection colors — blue is
 * selection, amber is urgent/unassigned, red is rejected. Readable on the dark theme.
 */
export const OPERATOR_NOTE_ACCENT = 'var(--color-tone-violet)';

/** True when the job carries a non-blank operator note. */
export function hasOperatorRequestNote(notes: string | null | undefined): notes is string {
  return notes != null && notes.trim() !== '';
}

export interface OperatorRequestNoteProps {
  readonly notes: string | null | undefined;
  /** Distinguishes multiple instances in one view (e.g. one per queue row). */
  readonly testId?: string;
}

export function OperatorRequestNote({
  notes,
  testId = 'operator-request-note',
}: OperatorRequestNoteProps): React.JSX.Element | null {
  if (!hasOperatorRequestNote(notes)) return null;
  return (
    <div
      data-testid={testId}
      role="note"
      aria-label={OPERATOR_NOTE_LABEL}
      style={{
        borderLeft: `3px solid ${OPERATOR_NOTE_ACCENT}`,
        backgroundColor: 'var(--color-bg-tertiary)',
        borderRadius: '4px',
        padding: '6px 10px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 700,
          color: OPERATOR_NOTE_ACCENT,
          marginBottom: '2px',
        }}
      >
        {OPERATOR_NOTE_LABEL}
        <span
          style={{
            marginLeft: '6px',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: 0,
            color: 'var(--color-text-tertiary)',
          }}
        >
          from the operator&apos;s system — not a dispatcher note
        </span>
      </div>
      <p
        data-testid={`${testId}-text`}
        style={{
          margin: 0,
          fontSize: '14px',
          color: 'var(--color-text-primary)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {notes}
      </p>
    </div>
  );
}
